import { NavLink, useLocation, useParams } from "react-router-dom";

import { Tabs, TabsList, TabsTrigger } from "./ui/tabs";
import { cn } from "../lib/utils";

/**
 * ProjectTabs renders the in-project navigation as a URL-driven tab
 * strip. The shadcn `Tabs` primitive supplies the visual language
 * (bordered pill, active underline); we use NavLink as the rendered
 * element so each tab is independently deep-linkable.
 */
export function ProjectTabs() {
  const { id } = useParams();
  const location = useLocation();
  const base = `/projects/${id}`;
  const tabs = [
    { to: base, label: "Overview", end: true, value: "overview" },
    { to: `${base}/reports`, label: "Reports", value: "reports" },
    { to: `${base}/api-keys`, label: "API keys", value: "api-keys" },
    { to: `${base}/webhooks`, label: "Webhooks", value: "webhooks" },
  ];

  return (
    <div className="border-b bg-card">
      <Tabs
        value={currentTabValue(tabs, location.pathname) ?? tabs[0]?.value}
        className={cn("mx-auto max-w-[88rem] px-4 md:px-8")}
      >
        <TabsList variant="line" className="h-11 w-full justify-start gap-6 overflow-x-auto bg-card p-0">
          {tabs.map((tab) => {
            const active = isActive(tab, location.pathname);
            return (
              <TabsTrigger
                key={tab.to}
                value={tab.value}
                render={
                  <NavLink
                    to={tab.to}
                    end={tab.end}
                    aria-selected={active}
                    role="tab"
                    data-testid={`project-tab-${tab.label.toLowerCase().replace(" ", "-")}`}
                  />
                }
                className={cn(
                  "relative h-11 rounded-none px-0 text-sm font-medium transition-colors",
                  active
                    ? "text-foreground after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:rounded-full after:bg-foreground after:content-['']"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {tab.label}
              </TabsTrigger>
            );
          })}
        </TabsList>
      </Tabs>
    </div>
  );
}

function isActive(
  tab: { to: string; end?: boolean },
  pathname: string,
): boolean {
  return tab.end
    ? pathname === tab.to
    : pathname === tab.to || pathname.startsWith(`${tab.to}/`);
}

function currentTabValue(
  tabs: ReadonlyArray<{ to: string; end?: boolean; value: string }>,
  pathname: string,
): string | undefined {
  // Pick the most specific match (longest `to`).
  const match = tabs
    .filter((tab) => isActive(tab, pathname))
    .sort((a, b) => b.to.length - a.to.length)[0];
  return match?.value;
}
